<template>
  <b-modal
    v-model="show"
    size="xl"
    title="ตั้งเป้าหมายรายได้"
    scrollable
    no-close-on-backdrop
    @show="load"
  >
    <div class="ts-modal">
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap: 12px">
        <div class="small text-muted">
          กำหนดเป้าต่อเดือนของทั้งคลินิกและรายสาขา · การเปลี่ยนแปลงจะปรากฏบนแดชบอร์ดภายในประมาณ 1 นาที
        </div>
        <b-form-select
          v-model="selectedYear"
          :options="yearOptions"
          size="sm"
          class="ts-year-select"
          @change="load"
        />
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center p-5"><b-spinner /></div>

      <!-- Error -->
      <div v-else-if="error" class="text-center p-5">
        <p class="text-danger">โหลดข้อมูลไม่สำเร็จ</p>
        <b-button size="sm" variant="outline-primary" @click="load">ลองใหม่</b-button>
      </div>

      <template v-else-if="model">
        <div class="table-responsive ts-card">
          <table class="ts-table">
            <thead>
              <tr>
                <th class="text-left">ขอบเขต</th>
                <th>เป้าต่อเดือน (฿)</th>
                <th>เป้าทั้งปี (คำนวณ)</th>
                <th class="text-left">รายเดือน</th>
              </tr>
            </thead>
            <tbody>
              <!-- Clinic row -->
              <tr class="ts-clinic-row">
                <td class="text-left scope-name">ทั้งคลินิก</td>
                <td>
                  <b-form-input
                    v-model.number="model.clinic.monthly"
                    type="number" min="0" step="10000" size="sm" class="ts-input"
                    placeholder="—"
                  />
                </td>
                <td class="ts-derived">{{ formatNumber(deriveYear(model.clinic)) }}</td>
                <td class="text-left">
                  <b-button size="sm" variant="link" class="p-0 ts-toggle" @click="toggle('clinic')">
                    {{ isOpen('clinic') ? 'ซ่อน' : 'กำหนดรายเดือน' }}
                    <span v-if="overrideCount(model.clinic)" class="ts-ovbadge">{{ overrideCount(model.clinic) }}</span>
                  </b-button>
                </td>
              </tr>
              <tr v-if="isOpen('clinic')">
                <td colspan="4" class="ts-overrides">
                  <div class="ts-override-grid">
                    <label v-for="m in 12" :key="m" class="ts-override-cell">
                      <span>{{ monthLabel(m) }}</span>
                      <b-form-input
                        v-model.number="model.clinic.overrides[m]"
                        type="number" min="0" step="10000" size="sm"
                        placeholder="ใช้ค่าต่อเดือน"
                      />
                    </label>
                  </div>
                </td>
              </tr>

              <!-- Branch rows -->
              <template v-for="b in branches">
                <tr :key="'b' + b.id" class="ts-branch-row">
                  <td class="text-left">
                    <div class="bname">{{ b.name }}</div>
                    <div class="bsub">/{{ b.code }}</div>
                  </td>
                  <td>
                    <b-form-input
                      v-model.number="model.branches[b.id].monthly"
                      type="number" min="0" step="10000" size="sm" class="ts-input"
                      placeholder="—"
                    />
                  </td>
                  <td class="ts-derived">{{ formatNumber(deriveYear(model.branches[b.id])) }}</td>
                  <td class="text-left">
                    <b-button size="sm" variant="link" class="p-0 ts-toggle" @click="toggle(b.id)">
                      {{ isOpen(b.id) ? 'ซ่อน' : 'กำหนดรายเดือน' }}
                      <span v-if="overrideCount(model.branches[b.id])" class="ts-ovbadge">{{ overrideCount(model.branches[b.id]) }}</span>
                    </b-button>
                  </td>
                </tr>
                <tr v-if="isOpen(b.id)" :key="'bo' + b.id">
                  <td colspan="4" class="ts-overrides">
                    <div class="ts-override-grid">
                      <label v-for="m in 12" :key="m" class="ts-override-cell">
                        <span>{{ monthLabel(m) }}</span>
                        <b-form-input
                          v-model.number="model.branches[b.id].overrides[m]"
                          type="number" min="0" step="10000" size="sm"
                          placeholder="ใช้ค่าต่อเดือน"
                        />
                      </label>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <template #modal-footer>
      <span v-if="hasChanges" class="small text-muted mr-auto">มีการเปลี่ยนแปลง {{ changeCount }} รายการ</span>
      <b-button variant="light" @click="show = false">ปิด</b-button>
      <b-button variant="primary" :disabled="!hasChanges || saving" @click="save">
        <b-spinner v-if="saving" small class="mr-1" />บันทึก
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import moment from "moment";
import { formatNumber } from "@/utils/utils";
import { groupTargetsToModel, deriveYearTotal, buildSaveItems } from "@/utils/kpiTargets";

const TH_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

// Year options: last year through two years ahead — targets are forward-looking,
// with the previous year kept available for late corrections. Pure (no instance state).
function buildYearOptions() {
  const years = [];
  const current = moment().year();
  for (let i = -1; i <= 2; i++) {
    const y = current + i;
    years.push({ value: y.toString(), text: `ปี ${y + 543}` });
  }
  return years;
}

export default {
  name: "TargetSettingModal",

  props: {
    // The clinic code (route param) the dashboard was loaded with.
    clinicUrl: { type: String, required: true },
  },

  data() {
    return {
      show: false,
      loading: false,
      saving: false,
      error: null,
      selectedYear: moment().year().toString(),
      yearOptions: buildYearOptions(),
      branches: [], // [{ id, name, code }]
      model: null, // { clinic, branches }
      baseline: null, // independent clone of model at load time, for diffing
      expanded: {}, // { [scopeKey]: true } — which override panels are expanded
    };
  },

  computed: {
    saveItems() {
      if (!this.model || !this.baseline) return [];
      return buildSaveItems(this.model, this.baseline, this.selectedYear);
    },
    hasChanges() {
      return this.saveItems.length > 0;
    },
    changeCount() {
      return this.saveItems.length;
    },
  },

  methods: {
    // Opened by the dashboard (e.g. via $refs). Loading is kicked off by the
    // modal's @show event so it always reflects the latest stored targets.
    open() {
      this.show = true;
    },
    formatNumber(val) {
      return formatNumber(val || 0);
    },
    monthLabel(m) {
      return TH_MONTHS[m - 1];
    },
    deriveYear(row) {
      return deriveYearTotal(row);
    },
    overrideCount(row) {
      return Object.values(row.overrides).filter((v) => Number(v) > 0).length;
    },
    isOpen(key) {
      return !!this.expanded[key];
    },
    toggle(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },

    async load() {
      this.error = null;
      this.loading = true;
      try {
        // 1) Full branch list (numeric ids + names) for this clinic.
        const res = await this.$store.dispatch("moduleUser/fetchClinicList");
        const clinic = ((res && res.data) || []).find((c) => c.code === this.clinicUrl);
        this.branches = (clinic && clinic.branches) || [];

        // 2) Stored targets for the year.
        const data = await this.$store.dispatch("dashboard/fetchTargets", {
          clinicUrl: this.clinicUrl,
          year: this.selectedYear,
        });

        const branchIds = this.branches.map((b) => b.id);
        this.model = groupTargetsToModel(data.targets, branchIds);
        this.baseline = groupTargetsToModel(data.targets, branchIds); // independent clone
        this.expanded = {};
      } catch (e) {
        this.error = e;
        this.$bvToast.toast("โหลดข้อมูลเป้าหมายไม่สำเร็จ", {
          variant: "danger", title: "ข้อผิดพลาด", autoHideDelay: 4000,
        });
      } finally {
        this.loading = false;
      }
    },

    async save() {
      this.saving = true;
      try {
        const result = await this.$store.dispatch("dashboard/saveTargets", {
          clinicUrl: this.clinicUrl,
          items: this.saveItems,
        });
        this.$bvToast.toast(
          `บันทึกแล้ว ${result.saved} รายการ · ล้าง ${result.cleared} รายการ`,
          { variant: "success", title: "สำเร็จ", autoHideDelay: 4000 }
        );
        await this.load(); // refresh baseline from the server
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.errorMessage || e.response.data.message)) ||
          "บันทึกไม่สำเร็จ";
        this.$bvToast.toast(msg, { variant: "danger", title: "ข้อผิดพลาด", autoHideDelay: 6000 });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ---- Cohesive with ClinicDashboard/BranchDashboard tokens ---- */
.ts-modal {
  --ts-primary: #2196f3;
  --ts-primary-dark: #1976d2;
  --ts-primary-light: #e3f2fd;
  --ts-green: #22c55e;
  --ts-bg: #f5f8fb;
  --ts-border: #e5eaf0;
  --ts-text-muted: #6b7280;
  --ts-ink: #1f2a37;
}
.ts-year-select { width: auto; min-width: 150px; font-weight: 600; }

/* Card wrapper — mirrors .kpi-card (flat, 1px border, 12px radius) */
.ts-card {
  background: #fff;
  border: 1px solid var(--ts-border);
  border-radius: 12px;
  overflow: hidden;
}

/* Table — mirrors .cd-branch-table */
.ts-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ts-table thead th {
  text-align: right;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ts-text-muted);
  padding: 10px 16px;
  border-bottom: 1px solid var(--ts-border);
  background: var(--ts-bg);
}
.ts-table thead th.text-left { text-align: left; }
.ts-table tbody td {
  padding: 12px 16px;
  text-align: right;
  border-bottom: 1px solid var(--ts-border);
  font-variant-numeric: tabular-nums;
  vertical-align: middle;
}
.ts-table tbody td.text-left { text-align: left; }
.ts-table tbody tr:last-child td { border-bottom: 0; }

/* Clinic row — faint primary tint so the clinic total reads as the parent */
.ts-clinic-row > td { background: #f4f9ff; }
.scope-name { font-weight: 700; color: var(--ts-ink); }
.bname { font-weight: 600; color: var(--ts-ink); }
.bsub { font-size: 11.5px; color: var(--ts-text-muted); font-weight: 400; }
.ts-derived { font-weight: 700; color: var(--ts-ink); }
.ts-input { max-width: 150px; margin-left: auto; text-align: right; font-variant-numeric: tabular-nums; }

/* Toggle link + override-count chip (chip mirrors .cd-seg--ghost .on) */
.ts-toggle { font-size: 12.5px; font-weight: 600; color: var(--ts-primary-dark); text-decoration: none; }
.ts-ovbadge {
  display: inline-block; min-width: 18px; padding: 0 6px; margin-left: 6px;
  border-radius: 999px; background: var(--ts-primary-light); color: var(--ts-primary-dark);
  font-size: 11px; font-weight: 700; text-align: center;
}

/* Override panel */
td.ts-overrides { background: var(--ts-bg); }
.ts-override-grid {
  display: grid; grid-template-columns: repeat(6, minmax(96px, 1fr)); gap: 12px; padding: 4px 0;
}
.ts-override-cell {
  display: flex; flex-direction: column; align-items: flex-start; gap: 3px; margin: 0;
  font-size: 11.5px; color: var(--ts-text-muted);
}
.ts-override-cell input { text-align: right; font-variant-numeric: tabular-nums; }
@media (max-width: 768px) {
  .ts-override-grid { grid-template-columns: repeat(3, 1fr); }
}
</style>
